import * as React from 'react';

import { cn } from '@cotera/client/v0/lib/utils';
import { useTiptapEditor } from '../../hooks/use-tiptap-editor';
import { useCommentAuthor } from '../../../../ui/tiptap/extensions/comment-mark';
import { Icon } from '../../../../ui/icon/icon';
import { scrollToCommentThread } from './comment-navigation';
import { useCommentMarginMode } from './comment-margin-host';
import { useActiveCommentId, useCommentThreads } from './comment-threads';
import { useCommentAnchorOffsets } from './comment-anchor-offsets';

/** Vertical nudge so a chip's centre lands on the line rather than its top. */
const LINE_OFFSET_PX = 2;

/**
 * A count in the margin beside every line that carries an open comment.
 *
 * The highlight in the prose says *these words* have a comment; it cannot say how many
 * replies are on it, and at the end of a long line it is easy to read past. The chip
 * is the thing you can aim at — and it is the only part of the feature that works the
 * same way in every view, including the ones too narrow for a column and the focus
 * view, which has no rail at all.
 *
 * Resolved threads are left out. A settled conversation is still in the document and
 * still in the rail's Resolved tab, but it is not something the margin should keep
 * pointing at.
 *
 * Clicking one puts the caret in the thread, which is the same act as picking it from
 * the rail: the thread opens wherever this document is showing threads.
 */
export function CommentMarginChips() {
  const { editor } = useTiptapEditor();
  const author = useCommentAuthor();
  const { threads } = useCommentThreads(editor);
  const activeCommentId = useActiveCommentId(editor);
  const marginMode = useCommentMarginMode();
  const layerRef = React.useRef<HTMLDivElement>(null);

  const open = React.useMemo(
    () => threads.filter((thread) => !thread.resolved),
    [threads]
  );
  const offsets = useCommentAnchorOffsets(editor, open, layerRef);

  // Comments are off entirely without an author, the same test the toolbar button
  // and the popovers use. And where the margin has room for the threads themselves,
  // a count beside them is labelling something already on screen.
  if (author === null || marginMode === 'cards') {
    return null;
  }

  return (
    <div
      ref={layerRef}
      // Spans the width and is anchored to the top, with no height of its own and
      // nothing clipped. The content box is only as tall as the pane, while the prose
      // inside it runs as long as the document — a layer bounded by that box would
      // cut off every chip past the first screenful.
      className="pointer-events-none absolute inset-x-0 top-0 z-10"
    >
      {open.map((thread) => {
        const top = offsets[thread.commentId];
        if (top === undefined) {
          return null;
        }
        return (
          <button
            key={thread.commentId}
            type="button"
            // Reachable by keyboard, and named. These were briefly hidden from
            // assistive tech and taken out of the tab order on the grounds that the
            // highlight in the prose and the comments index are two other ways in —
            // but a button nobody can reach is not a shortcut, it is a control that
            // works for some people and not others, and `aria-hidden` over something
            // clickable is the wrong promise either way.
            aria-label={`Open comment thread, ${thread.replies.length} message${
              thread.replies.length === 1 ? '' : 's'
            }`}
            title={`${thread.replies.length} comment${
              thread.replies.length === 1 ? '' : 's'
            }`}
            style={{ top: top - LINE_OFFSET_PX }}
            onClick={() => {
              if (editor !== null) {
                scrollToCommentThread(editor, thread.commentId);
              }
            }}
            className={cn(
              // `right-3` matches the `px-3` on the panel bar above, so the chips
              // line up with the collapse and share controls at the top of the pane
              // rather than standing a few pixels proud of them in a column of their
              // own. Change that padding and change this.
              'pointer-events-auto absolute right-3 flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              thread.commentId === activeCommentId
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
            )}
          >
            <Icon icon="message-square-more" className="h-3.5 w-3.5" />
            {thread.replies.length}
          </button>
        );
      })}
    </div>
  );
}
